import React, { useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { Search, Plus, Building2, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * HubSpot-style company picker: search existing or create new inline.
 * Props:
 *  - value: company_id (string) currently selected
 *  - onChange: (company_id, company_obj | null) => void
 *  - placeholder: optional
 */
export default function CompanyPicker({ value, onChange, placeholder = 'Search or add a company…' }) {
  const [companies, setCompanies] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    apiGet('/crm/companies')
      .then(r => setCompanies(r.companies || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (value && companies.length) {
      const c = companies.find(c => c.id === value);
      if (c) setSelected(c);
    } else if (!value) {
      setSelected(null);
    }
  }, [value, companies]);

  // Outside click closes the dropdown
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = query.trim()
    ? companies.filter(c => c.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 8)
    : companies.slice(0, 8);

  const exactMatch = companies.find(c => c.name.toLowerCase() === query.trim().toLowerCase());
  const canCreate = query.trim().length >= 2 && !exactMatch;

  const pick = (c) => {
    setSelected(c);
    onChange && onChange(c.id, c);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    setSelected(null);
    onChange && onChange('', null);
    setQuery('');
  };

  const createInline = async () => {
    if (!query.trim()) return;
    setCreating(true);
    try {
      const created = await apiPost('/crm/companies', { name: query.trim(), country: 'India', tags: [] });
      toast.success(`Company "${created.name}" added`);
      setCompanies(prev => [created, ...prev]);
      pick(created);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid="company-picker">
      {selected ? (
        <div className="flex items-center gap-2 input bg-indigo-50/40 border-[#3b4872]/30">
          <Building2 size={14} className="text-[#3b4872] shrink-0" />
          <span className="font-display font-semibold text-sm text-[#0F172A] flex-1 truncate">{selected.name}</span>
          {selected.industry && <span className="text-[10px] text-slate-500 truncate">{selected.industry}</span>}
          <button type="button" onClick={clear} className="text-slate-400 hover:text-red-600 text-xs px-1.5 font-bold" data-testid="company-picker-clear">
            ✕
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="input pl-9"
            data-testid="company-picker-search"
            autoComplete="off"
          />
        </div>
      )}

      {open && !selected && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid="company-picker-dropdown">
          {filtered.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {filtered.map(c => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => pick(c)}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3"
                    data-testid={`company-picker-option-${c.id}`}
                  >
                    <div className="w-8 h-8 rounded-sm bg-indigo-50 text-[#3b4872] flex items-center justify-center font-display font-bold text-xs shrink-0">
                      {(c.name || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-sm text-[#0F172A] truncate">{c.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {[c.industry, c.city].filter(Boolean).join(' · ') || '—'}
                        {(c.contact_count || 0) > 0 && ` · ${c.contact_count} contacts`}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {canCreate && (
            <button
              type="button"
              onClick={createInline}
              disabled={creating}
              className="w-full text-left px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 border-t border-slate-200 flex items-center gap-3"
              data-testid="company-picker-create"
            >
              <div className="w-8 h-8 rounded-sm bg-emerald-600 text-white flex items-center justify-center shrink-0">
                <Plus size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-sm text-emerald-800">
                  {creating ? 'Adding…' : <>Add new company <span className="font-bold">"{query.trim()}"</span></>}
                </div>
                <div className="text-[11px] text-emerald-700/80">Creates the record so you can finish details later</div>
              </div>
            </button>
          )}

          {filtered.length === 0 && !canCreate && (
            <div className="px-3 py-6 text-center text-xs text-slate-500">
              {query.trim() ? 'No matches. Type 2+ characters to add new.' : 'Start typing to search…'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
